import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import {
  answerBlock,
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionDropDownField, QuestionTextField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion, isBlockFullyValid } from 'components/Questionnaires/validators';
import { sortArrayByLabel } from 'common/language/intlArrayHelper';

const NOT_APPLICABLE = 'Not Applicable';

export const QuestionTreatySpecialRatesClaimBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [answer, setAnswer] = useState(block?.answers ?? {});
  const qAnswers = useSelector(state => state?.questionnaires?.formQuestionnaire?.answers);
  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const provisions = useSelector(state => state?.app?.specialRateProvisions);

  const currentLocale = intl.locale;

  const getIncomeTypes = () => {
    if (!provisions) {
      return [];
    }

    const defaultIncomeTypes = provisions
      .map(item => item.incomeType)
      .filter((value, index, self) => self.indexOf(value) === index)
      .map(item => Object.assign({ value: item, label: item }));

    let incomeTypes = defaultIncomeTypes;

    if (currentLocale !== 'en') {
      const intlItems = intl.messages['questionnaire.treatySpecialRatesClaim.incomeType.items'];
      try {
        const translatedMap = intlItems.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {});

        incomeTypes = defaultIncomeTypes.map((option) => {
          const translatedLabel = translatedMap[option.value];
          const finalLabel = translatedLabel
            ? `${ translatedLabel } (${ option.label })`
            : option.label;

          return {
            ...option,
            label: finalLabel
          };
        });
      } catch (error) {
        incomeTypes = defaultIncomeTypes; // Fallback to English on error
      }
    }

    const combinedArray = [{ value: '', label: intl.formatMessage({ id: 'questionnaire.treatySpecialRatesClaim.incomeType.placeholder' }) }, ...incomeTypes];

    return sortArrayByLabel(combinedArray, '', 'Other Income');
  };

  const getArticles = (incomeType) => {
    if (!incomeType || !provisions) {
      return [];
    }
    const articles = provisions
      .filter(x => x.incomeType === incomeType)
      .map(item => (item.article === '' ? NOT_APPLICABLE : item.article))
      .filter((value, index, self) => self.indexOf(value) === index)
      .map(item => Object.assign({ value: item, label: item }));

    if (articles.length === 1 && articles[0].value === NOT_APPLICABLE) {
      return articles;
    }

    return [{ value: '', label: intl.formatMessage({ id: 'questionnaire.treatySpecialRatesClaim.articleParagraph.placeholder' }) }, ...articles];
  };

  const getRates = (incomeType, article) => {
    if (!incomeType || !article || !provisions) {
      return [];
    }

    const articleOverride = article === NOT_APPLICABLE ? '' : article;
    const rates = provisions
      .filter(x => x.incomeType === incomeType && x.article === articleOverride)
      .map(item => item.percentage.toString())
      .filter((value, index, self) => self.indexOf(value) === index)
      .map(item => Object.assign({ value: item, label: `${ item }%` }));

    return [{ value: '', label: '...' }, ...rates];
  };

  const getRequiredValidator = errorMessage => ({ validation: {
    conditions: [
      { condition: requiredValidator, message: errorMessage }
    ]
  } });

  const [incomeType, setIncomeType] = useState({
    id: 'incomeType',
    label: 'questionnaire.treatySpecialRatesClaim.incomeType.label',
    component: QuestionDropDownField,
    componentProps: {
      items: getIncomeTypes()
    },
    otherValue: 'Other Income',
    className: 'col-5',
    visible: true,
    answer: answer?.incomeType,
    ...getRequiredValidator('questionnaire.treatySpecialRatesClaim.incomeType.errorMessage')
  });
  const [articleParagraph, setArticleParagraph] = useState({
    id: 'articleParagraph',
    label: 'questionnaire.treatySpecialRatesClaim.articleParagraph.label',
    component: QuestionDropDownField,
    componentProps: {
      items: [],
      preventTranslation: true
    },
    className: 'col-5 pl-0',
    visible: true,
    answer: answer?.articleParagraph,
    disabled: true
  });
  const [rate, setRate] = useState({
    id: 'rate',
    label: 'questionnaire.treatySpecialRatesClaim.rate.label',
    component: QuestionDropDownField,
    componentProps: {
      items: [],
      preventTranslation: true
    },
    className: 'col-2 pl-0',
    visible: true,
    answer: answer?.rate,
    disabled: true
  });
  const [explanation, setExplanation] = useState({
    id: 'explanation',
    label: 'questionnaire.treatySpecialRatesClaim.explanation.label',
    component: QuestionTextField,
    visible: true,
    answer: answer?.explanation,
    ...getRequiredValidator('questionnaire.treatySpecialRatesClaim.explanation.errorMessage')
  });

  const isBlockValid = () => {
    const questions = [incomeType, articleParagraph, rate, explanation];
    return isBlockFullyValid(null, questions, block?.answers, qAnswers);
  };

  const updateDependentQuestion = (existingQuestion, newAnswer, setState, isDisabled, items, errorMessage) => {
    const newValidation = isDisabled
      ? { validation: { conditions: undefined } }
      : getRequiredValidator(errorMessage);

    const newQuestion = {
      ...existingQuestion,
      answer: newAnswer,
      isValid: undefined,
      errorMessages: undefined,
      componentProps: { ...existingQuestion.componentProps, items },
      disabled: isDisabled,
      ...newValidation
    };
    validateQuestion(newQuestion);
    setState(newQuestion);

    return newQuestion;
  };

  const updateArticle = (incomeTypeAnswer) => {
    const items = getArticles(incomeTypeAnswer);
    let newAnswer = '';
    if (items.length === 1 && items[0].value === NOT_APPLICABLE) {
      newAnswer = NOT_APPLICABLE;
    } else if (items.length === 2 && items[0].value === '') {
      newAnswer = items[1].value;
    }

    const newArticle = updateDependentQuestion(
      articleParagraph,
      newAnswer,
      setArticleParagraph,
      !incomeTypeAnswer || newAnswer !== '',
      getArticles(incomeTypeAnswer),
      'questionnaire.treatySpecialRatesClaim.articleParagraph.errorMessage'
    );

    return newArticle;
  };

  const updateRate = (incomeTypeAnswer, articleAnswer) => {
    const items = getRates(incomeTypeAnswer, articleAnswer);
    let newAnswer = '';
    if (items.length === 2 && items[0].value === '') {
      newAnswer = items[1].value;
    }

    const newRate = updateDependentQuestion(
      rate,
      newAnswer,
      setRate,
      !articleAnswer || newAnswer !== '',
      items,
      'questionnaire.treatySpecialRatesClaim.rate.errorMessage'
    );

    return newRate;
  };

  const onQuestionChange = (q, value, setAns = true) => {
    switch (q.id) {
      case 'incomeType': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setIncomeType(newQuestion);
        const newArticleParagraph = updateArticle(value);
        const newRate = updateRate(value, newArticleParagraph?.answer);
        if (setAns) {
          setAnswer({ ...answer, incomeType: value, articleParagraph: newArticleParagraph?.answer, rate: newRate?.answer });
        }
        break;
      }
      case 'articleParagraph': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setArticleParagraph(newQuestion);
        const newRate = updateRate(incomeType?.answer, value);
        if (setAns) {
          setAnswer({ ...answer, articleParagraph: value, rate: newRate?.answer });
        }
        break;
      }
      case 'rate': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setRate(newQuestion);
        if (setAns) {
          setAnswer({ ...answer, rate: value });
        }
        break;
      }
      case 'explanation': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setExplanation(newQuestion);
        if (setAns) {
          setAnswer({ ...answer, explanation: value });
        }
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    const isDisabled = !provisions;
    const newIncomeType = {
      ...incomeType,
      answer: undefined,
      isValid: undefined,
      errorMessages: undefined,
      componentProps: {
        ...incomeType.componentProps,
        items: getIncomeTypes()
      },
      disabled: isDisabled,
    };

    onQuestionChange(newIncomeType, undefined, false);

    const newExplanation = { ...explanation, answer: undefined, isValid: undefined, errorMessages: undefined };
    onQuestionChange(newExplanation, undefined, false);

    setAnswer({ ...answer, incomeType: undefined, explanation: undefined });
  }, [provisions]);

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isBlockValid()));
    } else {
      onAnswer(block, answer, isBlockValid());
    }
  }, [answer]);

  useEffect(() => {
    if (incomeType.answer !== block.answers?.incomeType) {
      onQuestionChange(incomeType, block.answers?.incomeType);
    }
    if (articleParagraph.answer !== block.answers?.articleParagraph) {
      onQuestionChange(articleParagraph, block.answers?.articleParagraph);
    }
    if (rate.answer !== block.answers?.rate) {
      onQuestionChange(rate, block.answers?.rate);
    }
    if (explanation.answer !== block.answers?.explanation) {
      onQuestionChange(explanation, block.answers?.explanation);
    }
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (!answer?.incomeType) {
        onQuestionChange({ ...incomeType, isForceEdited: true }, '');
      }
      if (!answer?.articleParagraph) {
        onQuestionChange({ ...articleParagraph, isForceEdited: true }, '');
      }
      if (!answer?.rate) {
        onQuestionChange({ ...rate, isForceEdited: true }, '');
      }
      if (!answer?.explanation) {
        onQuestionChange({ ...explanation, isForceEdited: true }, '');
      }
    }

    if (block.isForceEdited === undefined) {
      if (incomeType.isForceEdited === true) {
        onQuestionChange({ ...incomeType, isForceEdited: undefined });
      }
      if (articleParagraph.isForceEdited === true) {
        onQuestionChange({ ...articleParagraph, isForceEdited: undefined });
      }
      if (rate.isForceEdited === true) {
        onQuestionChange({ ...rate, isForceEdited: undefined });
      }
      if (explanation.isForceEdited === true) {
        onQuestionChange({ ...explanation, isForceEdited: undefined });
      }
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <div className="row">
      <Question key={ incomeType.id } blockId={ blockIdentifier } question={ incomeType } { ...incomeType.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
      <Question key={ articleParagraph.id } blockId={ blockIdentifier } question={ articleParagraph } { ...articleParagraph.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
      <Question key={ rate.id } blockId={ blockIdentifier } question={ rate } { ...rate.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    </div>
    <div className="row">
      <Question key={ explanation.id } blockId={ blockIdentifier } question={ explanation } { ...explanation.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    </div>
  </React.Fragment>);
};

QuestionTreatySpecialRatesClaimBlock.displayName = 'QuestionTreatySpecialRatesClaim';

QuestionTreatySpecialRatesClaimBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
};

QuestionTreatySpecialRatesClaimBlock.defaultProps = {
  onAnswer: null,
};

export default QuestionTreatySpecialRatesClaimBlock;
